import React, { useEffect, useMemo, useState } from "react";
import { Form } from "react-bootstrap";
import Label from "../Label/Label";
import CustomButton from "../CustomButton/CustomButton";
import ModalPopup from "../ModalPopup/ModalPopup";
import AboutUsContent from "../AboutUsContent/AboutUsContent";
import {
  addAboutUs,
  fetchAboutUs,
  updateAboutUs,
  uploadAboutUs,
} from "../../services/aboutUsApi";
import {
  assertAdminAuth,
  formatDisplayDate,
  getApiErrorMessage,
} from "../../services/apiClient";
import {
  normalizeAboutUsDisplay,
  SAMPLE_ABOUT_US_JSON,
  validateAboutUsJson,
} from "../../util/aboutUsHelpers";
import "./AboutUsAdminTab.scss";

const AboutUsAdminTab = ({ labels = {} }) => {
  const [aboutUsResponse, setAboutUsResponse] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", subTitle: "" });
  const [aboutJsonFile, setAboutJsonFile] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const aboutDisplay = useMemo(
    () => normalizeAboutUsDisplay(aboutUsResponse),
    [aboutUsResponse],
  );

  const loadAboutUs = async () => {
    if (!assertAdminAuth()) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setLoadError("");

    try {
      const response = await fetchAboutUs();
      setAboutUsResponse(response);
      setEditForm({
        title: response?.data?.title || "",
        subTitle: response?.data?.subTitle || "",
      });
    } catch (error) {
      console.error("Failed to load about us data", error);
      setAboutUsResponse(null);
      setLoadError(
        getApiErrorMessage(
          error,
          "Failed to load about us data. Ensure you are logged in as admin.",
        ),
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAboutUs();
  }, []);

  const openUploadModal = () => {
    if (!assertAdminAuth()) {
      return;
    }

    setAboutJsonFile(null);
    setShowUploadModal(true);
  };

  const handleAboutFileChange = (event) => {
    setAboutJsonFile(event.target.files?.[0] || null);
  };

  const handleUploadAboutUs = async () => {
    if (!aboutJsonFile) {
      window.alert("Please select an About Us JSON file.");
      return;
    }

    setIsSaving(true);

    try {
      const fileText = await aboutJsonFile.text();
      const parsedAboutUs = JSON.parse(fileText);

      if (!validateAboutUsJson(parsedAboutUs)) {
        window.alert(
          "About Us JSON must include title, subTitle, cardData[], ourLeadership, and someDigipankians[].",
        );
        return;
      }

      let uploadedAboutUs = await uploadAboutUs(parsedAboutUs);

      setAboutUsResponse(uploadedAboutUs);
      setEditForm({
        title: uploadedAboutUs?.data?.title || "",
        subTitle: uploadedAboutUs?.data?.subTitle || "",
      });
      setShowUploadModal(false);
      setAboutJsonFile(null);
      window.alert("About Us data uploaded successfully.");
    } catch (error) {
      if (error?.response?.status === 409) {
        window.alert(
          "About Us data already exists. Use Upload to replace the full JSON.",
        );
        return;
      }

      window.alert(getApiErrorMessage(error, "Failed to upload About Us JSON."));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveEdits = async () => {
    if (!assertAdminAuth()) {
      return;
    }

    if (!editForm.title.trim() || !editForm.subTitle.trim()) {
      window.alert("Title and subtitle are required.");
      return;
    }

    setIsSaving(true);

    try {
      const updatedAboutUs = await updateAboutUs({
        title: editForm.title.trim(),
        subTitle: editForm.subTitle.trim(),
      });

      setAboutUsResponse(updatedAboutUs);
      setEditForm({
        title: updatedAboutUs?.data?.title || editForm.title,
        subTitle: updatedAboutUs?.data?.subTitle || editForm.subTitle,
      });
      window.alert("About Us updated successfully.");
    } catch (error) {
      window.alert(getApiErrorMessage(error, "Failed to update About Us."));
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddInitialData = async () => {
    if (!assertAdminAuth()) {
      return;
    }

    setIsSaving(true);

    try {
      const parsedAboutUs = JSON.parse(SAMPLE_ABOUT_US_JSON);
      const createdAboutUs = await addAboutUs(parsedAboutUs);
      setAboutUsResponse(createdAboutUs);
      setEditForm({
        title: createdAboutUs?.data?.title || "",
        subTitle: createdAboutUs?.data?.subTitle || "",
      });
      window.alert("About Us data created successfully.");
    } catch (error) {
      window.alert(getApiErrorMessage(error, "Failed to create About Us data."));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="aboutUs_Admin_Tab">
      <div className="aboutUs_Admin_Header">
        <Label title={labels.title || "About Us"} className="heading" />
        <div className="aboutUs_Admin_Actions">
          <CustomButton
            title={labels.uploadBtn || "Upload About Us JSON"}
            onClick={openUploadModal}
          />
          <CustomButton
            title={isSaving ? "Saving..." : labels.editBtn || "Save Changes"}
            onClick={handleSaveEdits}
          />
        </div>
      </div>

      {aboutUsResponse?.updatedAt && (
        <Label
          title={`Last updated: ${formatDisplayDate(aboutUsResponse.updatedAt)}`}
          className="aboutUs_Updated"
        />
      )}

      {aboutDisplay?.isLocalFallback && (
        <Label
          title="Showing local sample About Us data (API unavailable or no data yet)."
          className="aboutUs_Fallback_Notice"
        />
      )}

      {loadError && <Label title={loadError} className="aboutUs_Load_Error" />}

      <div className="aboutUs_Edit_Section">
        <Label title="Quick Edit" className="section_Heading" />
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              value={editForm.title}
              onChange={(event) =>
                setEditForm((current) => ({
                  ...current,
                  title: event.target.value,
                }))
              }
              placeholder="About Digipank"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Subtitle</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={editForm.subTitle}
              onChange={(event) =>
                setEditForm((current) => ({
                  ...current,
                  subTitle: event.target.value,
                }))
              }
              placeholder="About page subtitle"
            />
          </Form.Group>
        </Form>
      </div>

      {!isLoading && !aboutUsResponse && (
        <div className="aboutUs_Empty_State">
          <Label title="No About Us data found in the API yet." />
          <CustomButton
            title={labels.addBtn || "Add About Us Data"}
            onClick={handleAddInitialData}
          />
        </div>
      )}

      <div className="aboutUs_Preview">
        <Label title="Preview" className="preview_Heading" />
        {isLoading ? (
          <Label title="Loading preview..." />
        ) : (
          <AboutUsContent aboutData={aboutDisplay} showContactFooter={false} />
        )}
      </div>

      <ModalPopup
        showModal={showUploadModal}
        onHide={() => setShowUploadModal(false)}
        onHideClick={() => setShowUploadModal(false)}
        onButtonClick={handleUploadAboutUs}
        headerTitle="Upload About Us JSON"
        buttonTitle={isSaving ? "Uploading..." : "Upload About Us JSON"}
      >
        <div className="inner_Modal">
          <Form.Group controlId="aboutUsJsonUpload">
            <Form.Label>Select About Us JSON file</Form.Label>
            <Form.Control
              type="file"
              accept=".json,application/json"
              onChange={handleAboutFileChange}
            />
          </Form.Group>
          <details className="aboutUs_Sample_Block">
            <summary>View sample About Us JSON</summary>
            <pre>{SAMPLE_ABOUT_US_JSON}</pre>
          </details>
          {aboutJsonFile && (
            <Label title={`Selected: ${aboutJsonFile.name}`} className="pt-2" />
          )}
        </div>
      </ModalPopup>
    </div>
  );
};

export default AboutUsAdminTab;
